import React, { useEffect, useMemo, useState } from 'react';
import { researchList, researchCreate, researchUpdate, researchDelete, cleanAiText } from '../lib/api';
import { Bookmark, Plus, Search, Tag, Pencil, Trash2, Save, Loader2, X, FileText, Lightbulb, Sparkles, Package, BookOpen, Headphones, PenSquare } from 'lucide-react';
import { toast } from 'sonner';

const KINDS = [
  { id: 'all',      label: 'All',       icon: Bookmark },
  { id: 'content',  label: 'Content',   icon: FileText },
  { id: 'product',  label: 'Product',   icon: Package },
  { id: 'pitch',    label: 'Pitches',   icon: Sparkles },
  { id: 'ebook',    label: 'Ebooks',    icon: BookOpen },
  { id: 'workbook', label: 'Workbooks', icon: PenSquare },
  { id: 'outline',  label: 'Outlines',  icon: Lightbulb },
  { id: 'draft',    label: 'Drafts',    icon: Pencil },
  { id: 'note',     label: 'Notes',     icon: Headphones },
];

export default function Research() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (q.trim()) params.q = q.trim();
      setItems(await researchList(params));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach(i => { c[i.kind] = (c[i.kind] || 0) + 1; });
    return c;
  }, [items]);

  const remove = async (id) => {
    if (!window.confirm('Delete this research item?')) return;
    try { await researchDelete(id); setItems(list => list.filter(x => x.id !== id)); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="research-page" className="max-w-[1240px] mx-auto">
      <header className="mb-6">
        <div className="flex items-center gap-2 text-[#22d3ee]"><Bookmark className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Research vault</span></div>
        <h1 className="font-display text-[36px] md:text-[48px] mt-2 leading-[1.02]">Turn AI chats into <span className="brand-gradient">content &amp; products</span>.</h1>
        <p className="text-white/65 text-[14px] mt-3 max-w-[760px]">Saved outputs from ReaperBot and your specialist agents live here. Tag them, edit them, turn them into ebooks, workbooks, release plans, or pitch drafts.</p>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {KINDS.map(k => (
          <button key={k.id} onClick={() => setKind(k.id)} className={`chip ${kind === k.id ? 'active' : ''}`} data-testid={`research-kind-${k.id}`}>
            <k.icon className="w-3.5 h-3.5 mr-1 inline" /> {k.label} {counts[k.id] ? <span className="text-white/50 ml-1">({counts[k.id]})</span> : null}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search title, body, tags…" className="input-field pl-9 w-[260px]" data-testid="research-search" />
        </div>
        <button onClick={() => setComposing(true)} className="btn-primary" data-testid="research-new-btn"><Plus className="w-4 h-4" /> New note</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-shell p-10 text-center">
          <Bookmark className="w-10 h-10 mx-auto text-white/25" />
          <h3 className="font-display text-[22px] mt-3">Nothing saved yet</h3>
          <p className="text-white/55 text-[13.5px] mt-1 max-w-[440px] mx-auto">Chat with ReaperBot or any unlocked specialist, then click <span className="inline-flex items-center gap-1 text-[#22d3ee]"><Bookmark className="w-3 h-3" /> Save to Research</span> on any response to drop it here.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(r => (
            <article key={r.id} className="card-shell p-4 flex flex-col hover-lift" data-testid={`research-card-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="chip text-[10px] uppercase">{r.kind}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(r)} className="text-white/50 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(r.id)} className="text-white/50 hover:text-[#ff6b8a]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display text-[17px] mt-2 leading-tight line-clamp-2">{r.title}</h3>
              <p className="text-[13px] text-white/70 mt-2 line-clamp-5 whitespace-pre-wrap flex-1">{cleanAiText(r.body)}</p>
              {r.tags && r.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2.5">
                  {r.tags.slice(0, 4).map(t => <span key={t} className="text-[10.5px] text-white/50 bg-white/5 px-1.5 py-0.5 rounded"><Tag className="w-2.5 h-2.5 inline mr-0.5" />{t}</span>)}
                </div>
              )}
              <div className="text-[11px] text-white/40 mt-2">From {r.source || 'manual'} · {new Date(r.updated_at || r.created_at).toLocaleDateString()}</div>
            </article>
          ))}
        </div>
      )}

      {(editing || composing) && (
        <EditorModal
          item={editing}
          onClose={() => { setEditing(null); setComposing(false); }}
          onSaved={() => { setEditing(null); setComposing(false); load(); }}
        />
      )}
    </div>
  );
}

function EditorModal({ item, onClose, onSaved }) {
  const [title, setTitle] = useState(item?.title || '');
  const [body, setBody] = useState(item?.body || '');
  const [kind, setKind] = useState(item?.kind || 'note');
  const [tagStr, setTagStr] = useState((item?.tags || []).join(', '));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim() || !body.trim()) return toast.error('Title and body required');
    setBusy(true);
    try {
      const tags = tagStr.split(',').map(s => s.trim()).filter(Boolean);
      if (item) await researchUpdate(item.id, { title, body, kind, tags });
      else await researchCreate({ title, body, kind, tags, source: 'manual' });
      toast.success('Saved');
      onSaved();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0c] border border-white/15 rounded-2xl w-full max-w-[720px] max-h-[90vh] overflow-y-auto p-6" data-testid="research-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[22px]">{item ? 'Edit note' : 'New research note'}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" className="input-field" maxLength={200} data-testid="research-title" />
          <select value={kind} onChange={e => setKind(e.target.value)} className="input-field" data-testid="research-kind-select">
            {KINDS.filter(k => k.id !== 'all').map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select>
          <input value={tagStr} onChange={e => setTagStr(e.target.value)} placeholder="tags, comma separated" className="input-field" data-testid="research-tags" />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={14} className="input-field resize-none font-mono text-[13px]" placeholder="Paste or write your research here…" data-testid="research-body" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="research-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
